/* eslint-disable */
// 掃描對位的視覺層：吃遮罩與關節點，畫出深紫剪影、就緒輪廓與問題關節點。
//
// 這支是從外部調校 repo 原封不動複製過來的，請勿改動內容，
// 上游更新時直接整支覆蓋。lint 因此在這裡整支關掉。
//
// 這支刻意不碰相機、不碰 DOM 查詢、不跑自己的迴圈，也沒有全域狀態，
// 要嵌進別的 app 只需要複製這一支檔案。用法見 docs/scan-overlay.md。
//
// 遮罩一律以 Uint8Array 傳入，不綁任何一版 MediaPipe。

// 設計稿的相機框是 342×609，框內人形高 534。
// 關節點與輪廓都是貼在人體上的標記，而全身入鏡時人體大小取決於畫面高度，
// 所以尺寸一律以框高換算；改用框寬的話，換成 4:3 的相機就會顯得偏大。
const DESIGN_HEIGHT = 609;

// 肘、腕、膝、踝，MediaPipe Pose 33 點中的索引。
// 設計稿的 Skeleton-errors 就是這 8 點，肩與髖不標。
// 這 8 點同時是錯誤點的預設集合，以及正確關節點（綠點）的候選集合 ——
// 扣掉呼叫端指出的錯誤點，剩下的就是位置已經對了的關節。
const TRACKED_JOINTS = [13, 14, 15, 16, 25, 26, 27, 28];

const DEFAULTS = {
  // --- 遮罩處理 ---
  // 信心值低於這個比例就當成背景
  threshold: 0.7,
  // 消退速度。愈小愈穩，但人離開畫面後剪影消得愈慢。
  // 設成 1 等於完全不平滑，只看引擎自己給的遮罩。
  smoothing: 0.15,
  // 出現時要比消退快幾倍。手移動到新位置時，那些像素是從 0 跳到 255，
  // 上升太慢的話爬不過門檻，移動中的手就整片畫不出來。
  riseRatio: 4,

  // --- 深紫剪影，對應設計稿的 CSS ---
  // radial-gradient(5128.78% 41.95% at 56.06% 40.07%, rgba(27,0,84,.9), rgba(0,3,17,.9))
  // 不透明度已含在顏色裡（規格的 Opacity 90%），繪製時不要再套 globalAlpha
  silhouetteInner: 'rgba(27, 0, 84, 0.9)',
  silhouetteOuter: 'rgba(0, 3, 17, 0.9)',
  silhouetteRadiusX: 51.2878, // 相對於寬度
  silhouetteRadiusY: 0.4195, // 相對於高度
  silhouetteCenterX: 0.5606,
  silhouetteCenterY: 0.4007,

  // --- 人形以外的背景壓暗，對應設計稿裡人形外的那層遮罩 ---
  backdrop: 'rgba(0, 0, 0, 0.5)',

  // --- 就緒輪廓線：4px solid #00FFC1（設計稿基準值，會經過 fromDesign 換算）---
  outlineColor: '#00FFC1',
  outlineWidth: 4,
  // 往一圈方向各偏移一次來加粗輪廓，次數愈多邊緣愈均勻
  outlineSteps: 12,

  // --- 錯誤關節點：20×20，外圈光暈 r=10 @ 30%，實心內核 r=5 ---
  // 只讓外圈脈動、內核固定，定位點才會一直清楚
  jointColor: '#FF0084',
  jointOuterRadius: 10,
  jointOuterMinAlpha: 0.1,
  jointOuterMaxAlpha: 0.3,
  jointOuterCycleMs: 900,
  jointInnerRadius: 5,

  // --- 位置正確的關節點：畫法與錯誤關節點完全相同，只換顏色 ---
  okJointColor: '#00FFC1', // 與就緒輪廓線同色
  okJointMinVisibility: 0.8, // 低於此可見度就不畫，側面被身體擋住的左側關節才不會亂標
};

function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/**
 * 建立一個綁在指定畫布上的視覺層。
 * 每個實例各自持有離屏畫布與時間平滑的狀態，開兩個也不會互相干擾。
 */
export function createScanOverlay(canvas, options = {}) {
  const settings = { ...DEFAULTS, ...options };
  const ctx = canvas.getContext('2d');

  // 離屏畫布只在尺寸改變時重建，逐格重新配置會拖慢畫面
  let shapeCanvas = null; // 放大到畫布尺寸的人形，供裁形使用
  let workCanvas = null; // 組裝用，組好才畫到主畫布
  let maskCanvas = null; // 遮罩的原生尺寸

  // 逐格重用，避免每秒配置數十個 MB
  let maskImage = null;
  let smoothBuffer = null; // 時間平滑的狀態，跨影格保留
  let maskPrimed = false;

  /** 把設計稿上的 px 換算成畫布上的 px。 */
  function fromDesign(value) {
    return (value * canvas.height) / DESIGN_HEIGHT;
  }

  /**
   * 把遮罩整理成乾淨又穩定的二值人形，直接寫進 maskImage 的 alpha。
   *
   * 分兩步。先做時間平滑：MediaPipe 每格獨立推論，同一個邊緣像素的信心值會逐格
   * 跳動，直接取閾值的話這些像素會不停在 0 與 255 之間翻轉，輪廓就是這樣抖起來的。
   * 再做十字五點表決：順手去掉背景的零星誤判、補起人體內的小破洞。
   *
   * 平滑是非對稱的：上升快、下降慢。對稱的話會兩頭落空 —— 想壓抖動就得放慢，
   * 一放慢，移動中的手因為每格都在新位置從 0 起爬，永遠爬不過門檻而整片消失。
   */
  function denoise(confidence, width, height) {
    const threshold = settings.threshold * 255;
    const fall = settings.smoothing;
    const rise = Math.min(1, fall * settings.riseRatio);
    const data = maskImage.data;
    const count = width * height;

    // 第一格沒有歷史可平均，直接當成現況，否則剪影會從全黑慢慢淡入
    if (!maskPrimed) {
      smoothBuffer.set(confidence);
      maskPrimed = true;
    }

    for (let i = 0; i < count; i += 1) {
      const previous = smoothBuffer[i];
      const target = confidence[i];
      smoothBuffer[i] = previous + (target - previous) * (target > previous ? rise : fall);
    }

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const i = y * width + x;
        const center = smoothBuffer[i];

        // 畫面外沿用中心值，人體被裁切時邊緣才不會被削掉一圈
        const left = x > 0 ? smoothBuffer[i - 1] : center;
        const right = x < width - 1 ? smoothBuffer[i + 1] : center;
        const up = y > 0 ? smoothBuffer[i - width] : center;
        const down = y < height - 1 ? smoothBuffer[i + width] : center;

        let votes = 0;
        if (center > threshold) votes += 1;
        if (left > threshold) votes += 1;
        if (right > threshold) votes += 1;
        if (up > threshold) votes += 1;
        if (down > threshold) votes += 1;

        data[i * 4 + 3] = votes >= 3 ? 255 : 0;
      }
    }
  }

  /**
   * 把遮罩畫成畫布尺寸的人形，只有 alpha 有意義，
   * 顏色留給後續裁形時決定。
   */
  function buildShape(confidence, width, height) {
    if (!maskCanvas || maskCanvas.width !== width || maskCanvas.height !== height) {
      maskCanvas = createCanvas(width, height);
      maskImage = maskCanvas.getContext('2d').createImageData(width, height);
      smoothBuffer = new Float32Array(width * height);
      maskPrimed = false;
    }

    // denoise 直接寫進 maskImage 的 alpha，中間不再多一趟複製
    denoise(confidence, width, height);
    maskCanvas.getContext('2d').putImageData(maskImage, 0, 0);

    // 遮罩解析度通常小於影像，放大到畫布尺寸才能對齊
    const shapeCtx = shapeCanvas.getContext('2d');
    shapeCtx.clearRect(0, 0, shapeCanvas.width, shapeCanvas.height);
    shapeCtx.drawImage(maskCanvas, 0, 0, shapeCanvas.width, shapeCanvas.height);

    return shapeCanvas;
  }

  /** 把工作畫布清乾淨並還原成預設的疊圖模式。 */
  function resetWorkCanvas() {
    const workCtx = workCanvas.getContext('2d');
    workCtx.setTransform(1, 0, 0, 1, 0, 0);
    workCtx.globalCompositeOperation = 'source-over';
    workCtx.clearRect(0, 0, workCanvas.width, workCanvas.height);
    return workCtx;
  }

  /** 把人形挖掉，剩下的背景壓上半透明黑，讓剪影與輪廓更突出。 */
  function drawBackdrop(shape) {
    const workCtx = resetWorkCanvas();

    workCtx.fillStyle = settings.backdrop;
    workCtx.fillRect(0, 0, workCanvas.width, workCanvas.height);

    workCtx.globalCompositeOperation = 'destination-out';
    workCtx.drawImage(shape, 0, 0);
    workCtx.globalCompositeOperation = 'source-over';

    ctx.drawImage(workCanvas, 0, 0);
  }

  /** 用深紫徑向漸層填滿畫布，再裁成人形後疊上去。 */
  function drawSilhouette(shape) {
    const { width, height } = workCanvas;
    const workCtx = resetWorkCanvas();

    // canvas 只有圓形漸層，先把 x 軸壓成與 y 軸同半徑，畫完再還原
    const radiusY = settings.silhouetteRadiusY * height;
    const scaleX = (settings.silhouetteRadiusX * width) / radiusY;
    const centerX = (settings.silhouetteCenterX * width) / scaleX;
    const centerY = settings.silhouetteCenterY * height;

    workCtx.scale(scaleX, 1);
    const gradient = workCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radiusY);
    gradient.addColorStop(0, settings.silhouetteInner);
    gradient.addColorStop(1, settings.silhouetteOuter);
    workCtx.fillStyle = gradient;
    workCtx.fillRect(0, 0, width / scaleX, height);
    workCtx.setTransform(1, 0, 0, 1, 0, 0);

    workCtx.globalCompositeOperation = 'destination-in';
    workCtx.drawImage(shape, 0, 0);
    workCtx.globalCompositeOperation = 'source-over';

    ctx.drawImage(workCanvas, 0, 0);
  }

  /** 把人形往四周撐開一圈再挖掉原形，剩下的外環就是輪廓線。 */
  function drawOutline(shape) {
    const { width, height } = workCanvas;
    const workCtx = resetWorkCanvas();
    const lineWidth = fromDesign(settings.outlineWidth);

    for (let step = 0; step < settings.outlineSteps; step += 1) {
      const angle = (step / settings.outlineSteps) * Math.PI * 2;
      workCtx.drawImage(shape, Math.cos(angle) * lineWidth, Math.sin(angle) * lineWidth);
    }

    workCtx.globalCompositeOperation = 'destination-out';
    workCtx.drawImage(shape, 0, 0);

    // 只換顏色，保留上一步留下的外環形狀
    workCtx.globalCompositeOperation = 'source-in';
    workCtx.fillStyle = settings.outlineColor;
    workCtx.fillRect(0, 0, width, height);
    workCtx.globalCompositeOperation = 'source-over';

    ctx.drawImage(workCanvas, 0, 0);
  }

  /** 標出一組關節點：外圈光暈脈動，實心內核不動。顏色以外的畫法紅綠一致。 */
  function drawJoints(landmarks, indexes, color) {
    const outerRadius = fromDesign(settings.jointOuterRadius);
    const innerRadius = fromDesign(settings.jointInnerRadius);

    const wave =
      (Math.sin((performance.now() / settings.jointOuterCycleMs) * Math.PI * 2) + 1) / 2;
    const outerAlpha =
      settings.jointOuterMinAlpha +
      wave * (settings.jointOuterMaxAlpha - settings.jointOuterMinAlpha);

    ctx.save();
    ctx.fillStyle = color;

    for (const index of indexes) {
      const point = landmarks[index];
      if (!point) {
        continue;
      }

      const x = point.x * canvas.width;
      const y = point.y * canvas.height;

      ctx.globalAlpha = outerAlpha;
      ctx.beginPath();
      ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(x, y, innerRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  return {
    /** 執行期調參，只覆寫給到的欄位。 */
    setOptions(partial) {
      Object.assign(settings, partial);
    },

    /** 目前生效的設定，唯讀用途。 */
    get options() {
      return { ...settings };
    },

    /**
     * 同步畫布尺寸。影像尺寸確定或改變時都要呼叫 —— 相機常在串流開始後才改變
     * 輸出尺寸，畫布沒跟上的話剪影與輪廓會整片偏離人體。
     */
    resize(width, height) {
      if (!width || !height) {
        return;
      }
      if (canvas.width === width && canvas.height === height) {
        return;
      }

      canvas.width = width;
      canvas.height = height;
      shapeCanvas = createCanvas(width, height);
      workCanvas = createCanvas(width, height);
    },

    /** 清掉時間平滑的歷史。人離開畫面、切換相機之後呼叫，剪影才不會殘留。 */
    reset() {
      maskPrimed = false;
    },

    /**
     * 畫一格。
     *
     * confidence   Uint8Array，0–255 的遮罩信心值；沒有遮罩就傳 null
     * maskWidth    遮罩寬
     * maskHeight   遮罩高
     * landmarks    選填，0–1 正規化座標的陣列
     * state        'default' 或 'error'
     * errorJoints  選填，要標紅的 landmark 索引；省略則用預設的肘腕膝踝 8 點。
     *              追蹤的 8 點裡沒被指名的，就是位置已經對了的關節，標綠。
     */
    render({
      confidence = null,
      maskWidth = 0,
      maskHeight = 0,
      landmarks = null,
      state = 'default',
      errorJoints = TRACKED_JOINTS,
    }) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const showingError = state === 'error';

      // 還沒 resize 過就先不畫，離屏畫布不存在時繪製會直接炸開
      if (confidence && shapeCanvas) {
        const shape = buildShape(confidence, maskWidth, maskHeight);
        drawBackdrop(shape);
        drawSilhouette(shape);

        // 規格的錯誤狀態「輪廓框：無」，問題交給關節點指出
        if (!showingError) {
          drawOutline(shape);
        }
      }

      if (showingError && landmarks) {
        // 追蹤點裡沒被指名為錯誤的就是已經對好的。可見度低的不畫 ——
        // 側面時被身體擋住的左側關節是引擎猜的，標綠會誤導。
        const okJoints = TRACKED_JOINTS.filter(
          index =>
            !errorJoints.includes(index) &&
            (landmarks[index]?.visibility ?? 1) >= settings.okJointMinVisibility
        );

        // 先綠後紅。兩組集合互斥，但呼叫端萬一傳了重疊的索引，該由紅點勝出
        drawJoints(landmarks, okJoints, settings.okJointColor);
        drawJoints(landmarks, errorJoints, settings.jointColor);
      }
    },
  };
}
